import React from 'react';

const sectionTitleClass = 'text-2xl font-semibold text-amber-100 mt-8 mb-4';
const paragraphClass = 'mb-6 leading-relaxed';
const listClass = 'list-disc pl-6 mb-6 space-y-2 text-zinc-400';

const Terms = () => {
  return (
    <div className="pt-32 pb-24 px-6 max-w-3xl mx-auto">
      <div className="mb-12">
        <h1 className="text-4xl md:text-5xl font-bold tracking-tight text-amber-50 mb-4">Terms of Service</h1>
        <p className="text-zinc-400">Last updated: June 28, 2026</p>
      </div>

      <div className="prose prose-invert prose-orange max-w-none text-zinc-300">
        <p className="mb-8 leading-relaxed text-zinc-300">
          Welcome to Moment of Truth. These Terms of Service govern your access to and use of our website,
          dashboard, review collection tools, public Wall of Love pages, widgets, badges, and related services.
          By using Moment of Truth, you agree to these Terms. If you do not agree, please do not use the service.
        </p>

        <h2 className={sectionTitleClass}>1. Agreement to Terms</h2>
        <p className={paragraphClass}>
          By creating an account or using Moment of Truth, you confirm that you are at least 18 years old, that
          you have the authority to accept these Terms on behalf of yourself or the business you represent, and
          that you will use the service only in accordance with these Terms and applicable laws.
        </p>

        <h2 className={sectionTitleClass}>2. About Moment of Truth</h2>
        <p className={paragraphClass}>
          Moment of Truth helps businesses collect, import, manage, sync, publish, and showcase customer reviews
          and testimonials from supported platforms and manual submissions. Our service may include review
          importing, review syncing, public Wall of Love pages, embeddable widgets, manual review collection,
          approval workflows, badges, and subscription-based premium features. We may add, change, or remove
          features over time.
        </p>

        <h2 className={sectionTitleClass}>3. Accounts and Security</h2>
        <p className={paragraphClass}>
          You are responsible for maintaining the security of your account and login credentials. You agree to
          provide accurate account information, keep your login details confidential, notify us if you suspect
          unauthorized access, and be responsible for activity that occurs under your account.
        </p>
        <p className={paragraphClass}>
          We are not liable for unauthorized access caused by your failure to protect your credentials.
        </p>

        <h2 className={sectionTitleClass}>4. Customer Content</h2>
        <p className={paragraphClass}>
          Customer Content includes any reviews, testimonials, ratings, customer names, company names, logos,
          images, profile photos, links, text, media, or other content that you import, sync, submit, upload,
          approve, publish, or display through Moment of Truth.
        </p>
        <p className={paragraphClass}>
          You retain ownership of your Customer Content. By using Moment of Truth, you grant us a limited license
          to host, store, process, format, display, reproduce, and distribute your Customer Content as needed to
          provide the service, including on your public Wall of Love page, widgets, dashboard, previews, and
          related product features.
        </p>

        <h2 className={sectionTitleClass}>5. Rights, Permissions, and Review Content</h2>
        <p className={paragraphClass}>
          You are solely responsible for ensuring that you have all necessary rights, permissions, consents, and
          approvals to import, sync, publish, display, or otherwise use any Customer Content through Moment of
          Truth.
        </p>
        <p className={paragraphClass}>
          This includes reviews, testimonials, ratings, customer names, job titles, company names, logos, profile
          photos, screenshots, and any content imported from third-party platforms or added manually. You must not
          use Moment of Truth to publish content that you do not have the right to use.
        </p>

        <h2 className={sectionTitleClass}>6. Review Authenticity</h2>
        <p className={paragraphClass}>
          Moment of Truth is built to help businesses showcase real customer trust. You agree not to use the
          service to create, publish, import, or display fake, misleading, manipulated, or deceptive reviews.
        </p>
        <ul className={listClass}>
          <li>Manually added reviews must be genuine.</li>
          <li>Reviews should reflect real customer experiences.</li>
          <li>You must not fabricate customer names, companies, ratings, or testimonials.</li>
          <li>You must not materially alter a review in a way that changes its meaning.</li>
          <li>You must not present AI-generated or fictional reviews as real customer reviews.</li>
          <li>You must not publish reviews in a way that is misleading to visitors, customers, or potential buyers.</li>
        </ul>
        <p className={paragraphClass}>
          You are responsible for the accuracy, legality, and authenticity of the reviews and testimonials you
          choose to publish.
        </p>

        <h2 className={sectionTitleClass}>7. Third-Party Platforms</h2>
        <p className={paragraphClass}>
          Moment of Truth may allow you to import or sync reviews from third-party platforms such as Google,
          Trustpilot, G2, Clutch, and other supported sources. These platforms are not controlled by us.
        </p>
        <p className={paragraphClass}>
          We do not guarantee that any third-party platform will always be available, accessible, supported, or
          compatible with Moment of Truth. Third-party platforms may change their APIs, page structure, policies,
          access rules, pricing, or technical restrictions at any time. These changes may affect our ability to
          import, sync, update, or display reviews from those platforms.
        </p>
        <p className={paragraphClass}>
          You are responsible for complying with the terms, policies, and rules of any third-party platform from
          which you import, sync, or display content. Moment of Truth is not affiliated with, endorsed by, or
          sponsored by any third-party review platform unless explicitly stated.
        </p>

        <h2 className={sectionTitleClass}>8. Public Pages, Widgets, and Badges</h2>
        <p className={paragraphClass}>
          Your published Wall of Love page and embedded widgets may be publicly accessible. Visitors may be able
          to view the reviews, testimonials, names, logos, ratings, and other content you choose to publish. You
          are responsible for deciding which reviews to publish or hide.
        </p>
        <p className={paragraphClass}>
          Some plans may include badges, including the Commitment to Truth badge. Unless explicitly stated, a
          badge does not mean that Moment of Truth has independently audited, verified, certified, or endorsed
          every review you publish.
        </p>
        <p className={paragraphClass}>
          We may remove, disable, or restrict public pages, widgets, badges, or content if we believe they violate
          these Terms, applicable laws, third-party rights, or the integrity of the platform.
        </p>

        <h2 className={sectionTitleClass}>9. Acceptable Use</h2>
        <p className={paragraphClass}>
          You agree to use Moment of Truth only for lawful business purposes. You must not use the service to:
        </p>
        <ul className={listClass}>
          <li>Publish illegal, harmful, fraudulent, or misleading content.</li>
          <li>Publish fake, deceptive, or manipulated reviews.</li>
          <li>Violate any third-party rights, including copyright, trademark, privacy, publicity, or data protection rights.</li>
          <li>Harass, threaten, defame, abuse, or impersonate others.</li>
          <li>Distribute spam, malware, phishing links, or malicious code.</li>
          <li>Reverse-engineer, copy, scrape, or steal our source code, systems, or product design.</li>
          <li>Interfere with the security, stability, or performance of the service.</li>
          <li>Use the service in a way that could damage the reputation, reliability, or legal standing of Moment of Truth.</li>
        </ul>
        <p className={paragraphClass}>
          We reserve the right to suspend or terminate accounts that violate these rules.
        </p>

        <h2 className={sectionTitleClass}>10. Plans, Limits, and Premium Features</h2>
        <p className={paragraphClass}>
          Moment of Truth may offer free and paid plans. Plan limits may include the number of published reviews,
          number of widgets, available widget designs, badge access, approval speed, support level, and other
          usage-based or feature-based limits.
        </p>
        <p className={paragraphClass}>
          We may update plan limits, pricing, or included features from time to time. If a change materially
          affects your paid subscription, we will make reasonable efforts to notify you. Some features may be
          marked as upcoming, beta, experimental, or early access. These features may change, break, or be removed
          before general release.
        </p>

        <h2 className={sectionTitleClass}>11. Billing and Subscriptions</h2>
        <p className={paragraphClass}>
          Payment processing is handled by our payment partner, Dodo Payments. Paid subscriptions are billed in
          advance on a monthly or yearly basis, depending on the plan you choose.
        </p>
        <p className={paragraphClass}>
          By subscribing to a paid plan, you authorize recurring billing until your subscription is cancelled. You
          are responsible for any applicable taxes, fees, or charges related to your subscription.
        </p>
        <p className={paragraphClass}>
          If payment fails, your subscription may be marked as unpaid, paused, downgraded, restricted, or
          cancelled. You may cancel your subscription using the cancellation options available in your account,
          payment portal, or by contacting support.
        </p>

        <h2 className={sectionTitleClass}>12. Refunds</h2>
        <p className={paragraphClass}>
          Refunds are handled on a case-by-case basis. To request a refund, contact our support team with your
          account details and reason for the request. We may approve or deny refund requests at our discretion,
          unless applicable law requires otherwise.
        </p>

        <h2 className={sectionTitleClass}>13. Support</h2>
        <p className={paragraphClass}>
          We may provide support through email, chat, dashboard support, or other available channels. Support
          availability and response times may vary based on your plan, workload, timezone, technical complexity,
          and the nature of your request. Priority support does not guarantee immediate resolution of every issue.
        </p>

        <h2 className={sectionTitleClass}>14. Service Availability</h2>
        <p className={paragraphClass}>
          We work to keep Moment of Truth reliable and available, but we do not guarantee uninterrupted,
          error-free, or always-available service. The service may be unavailable due to maintenance, updates,
          technical issues, internet failures, third-party provider issues, or events outside our control.
        </p>
        <p className={paragraphClass}>
          We are not responsible for losses caused by downtime, sync failures, third-party platform changes, or
          temporary unavailability.
        </p>

        <h2 className={sectionTitleClass}>15. Privacy and Data</h2>
        <p className={paragraphClass}>
          Your use of Moment of Truth is also governed by our Privacy Policy. By using the service, you agree that
          we may collect, process, and store information as described in our Privacy Policy.
        </p>
        <p className={paragraphClass}>
          You are responsible for ensuring that your use of Moment of Truth complies with any privacy, data
          protection, or consent requirements that apply to your business and customers.
        </p>

        <h2 className={sectionTitleClass}>16. Intellectual Property and Feedback</h2>
        <p className={paragraphClass}>
          Moment of Truth, including our software, design, branding, interface, logo, features, workflows, and
          underlying technology, is owned by us or our licensors. You may not copy, modify, reproduce, distribute,
          sell, lease, reverse-engineer, or create derivative works from Moment of Truth unless we give you
          written permission.
        </p>
        <p className={paragraphClass}>
          If you send us ideas, suggestions, bug reports, feature requests, or other feedback, you allow us to use
          that feedback without restriction or compensation to you.
        </p>

        <h2 className={sectionTitleClass}>17. Termination</h2>
        <p className={paragraphClass}>
          You may stop using Moment of Truth at any time. We may suspend or terminate your access if you violate
          these Terms, your payment fails, your use creates legal, security, reputational, or operational risk, we
          are required to do so by law, or we discontinue the service or a material part of it.
        </p>
        <p className={paragraphClass}>
          After termination, we may delete, restrict, or retain account data as described in our Privacy Policy
          and as required by law or legitimate business needs.
        </p>

        <h2 className={sectionTitleClass}>18. Disclaimer and Limitation of Liability</h2>
        <p className={paragraphClass}>
          Moment of Truth is provided as is and as available. To the maximum extent permitted by law, we make no
          warranties, express or implied, including warranties of merchantability, fitness for a particular
          purpose, availability, accuracy, non-infringement, or that the service will meet your specific business
          needs.
        </p>
        <p className={paragraphClass}>
          We do not guarantee that using Moment of Truth will increase conversions, sales, trust, traffic, search
          rankings, or revenue.
        </p>
        <p className={paragraphClass}>
          To the maximum extent permitted by law, Moment of Truth and its owners, employees, contractors,
          partners, and service providers will not be liable for indirect, incidental, special, consequential,
          exemplary, or punitive damages, including loss of revenue, profits, customers, leads, data, goodwill, or
          business interruption.
        </p>
        <p className={paragraphClass}>
          Our total liability for any claim related to the service will not exceed the amount you paid to Moment
          of Truth in the three months before the claim arose.
        </p>

        <h2 className={sectionTitleClass}>19. Indemnity</h2>
        <p className={paragraphClass}>
          You agree to defend, indemnify, and hold harmless Moment of Truth from any claims, damages, losses,
          liabilities, costs, or expenses arising from your use of the service, your Customer Content, your
          published reviews, testimonials, widgets, public pages, your violation of these Terms, your violation of
          applicable law, your violation of third-party rights, or your failure to obtain required permissions or
          consents.
        </p>

        <h2 className={sectionTitleClass}>20. Changes to These Terms</h2>
        <p className={paragraphClass}>
          We may update these Terms from time to time. If we make material changes, we will make reasonable
          efforts to notify you, such as by email, dashboard notice, or website update. Your continued use of
          Moment of Truth after changes become effective means you accept the updated Terms.
        </p>

        <h2 className={sectionTitleClass}>21. Governing Law and Jurisdiction</h2>
        <p className={paragraphClass}>
          These Terms are governed by the laws of India. The courts of Delhi, located in New Delhi, India, will
          have exclusive jurisdiction over disputes arising from or related to these Terms or your use of Moment
          of Truth, unless applicable law requires otherwise.
        </p>

        <h2 className={sectionTitleClass}>22. Contact</h2>
        <p className={paragraphClass}>
          If you have questions about these Terms, contact us at{' '}
          <a href="mailto:help@momentoftruth.io" className="text-orange-300 hover:text-orange-200">
            help@momentoftruth.io
          </a>
          .
        </p>
      </div>
    </div>
  );
};

export default Terms;